import React from "react";
import { ArrowRight, CalendarClock } from "lucide-react";
import ServicePageTemplate from "@/components/common/ServicePageTemplate";
import SectionHeading from "@/components/common/SectionHeading";
import Icon from "@/components/common/Icon";
import TrackShipment from "@/components/common/TrackShipment";
import { Reveal, Stagger, StaggerItem } from "@/components/common/Reveal";
import { CTAButton } from "@/components/common/CTA";
import { logisticsSolutions } from "@/config/site";

export default function Logistics() {
  return (
    <ServicePageTemplate
      seo={{
        title: "Logistics Coordination | Premier Global Sourcing",
        description: "Coordinate air, sea or land transport options with qualified partners. Request a freight rate and track your enquiry status.",
        path: "/logistics-coordination",
      }}
      breadcrumbs={[{ label: "Logistics Coordination" }]}
      eyebrow="Logistics Coordination"
      title="Transport options, coordinated with the right partners"
      intro="Coordinate with relevant service providers to evaluate available air, sea or land transport options based on the cargo, origin and destination."
      leadCopy="We are not a courier company and do not own vehicles or warehouses. Instead, we coordinate with relevant qualified service providers so transport options can be evaluated for your specific cargo and route."
      image="https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100"
      imageAlt="Warehouse and distribution operations with organized inventory"
      bullets={[
        "Evaluate available air, sea or land transport options",
        "Coordination based on cargo, origin and destination",
        "Freight, inspection and customs arranged via qualified partners where applicable",
        "Kept connected to your sourcing and trading requirement",
      ]}
      note="Freight, inspection, customs clearance and other regulated services may be coordinated through relevant qualified partners where applicable. We do not represent ourselves as a licensed freight carrier or customs broker."
    >
      {/* Logistics services & solutions */}
      <section className="container-page py-20 lg:py-28" id="solutions">
        <SectionHeading
          eyebrow="Services & solutions"
          title="Logistics services and solutions"
          intro="Whatever your commodity or route, we coordinate a suitable transport approach with qualified partners."
        />
        <Stagger className="mt-12 grid gap-px bg-[#D9E0E4] sm:grid-cols-2 lg:grid-cols-3 border border-[#D9E0E4] rounded-lg overflow-hidden">
          {logisticsSolutions.map((s) => (
            <StaggerItem key={s.title}>
              <div className="h-full bg-white p-7">
                <div className="h-11 w-11 rounded-md bg-[#EAF1F4] flex items-center justify-center">
                  <Icon name={s.icon} className="h-5 w-5 text-teal" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-[17px] text-navy">{s.title}</h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground">{s.copy}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal className="mt-10">
          <CTAButton to="/rate-quote" variant="primary" icon={ArrowRight} data-testid="logistics-rate-cta">
            Request a freight rate
          </CTAButton>
        </Reveal>
      </section>

      {/* Track & schedules */}
      <section className="bg-alt border-y border-[#D9E0E4]" id="tracking">
        <div className="container-page py-20 lg:py-28">
          <div className="grid gap-10 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Track & schedules"
                title="Check your enquiry status"
                intro="Enter your reference number to see where your request is in our coordination process. Sailing schedules and shipment milestones are coordinated with the relevant partner per booking."
              />
            </div>
            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="rounded-lg border border-[#D9E0E4] bg-white p-6 lg:p-8">
                <div className="flex items-center gap-2 mb-4">
                  <CalendarClock className="h-5 w-5 text-teal" />
                  <h3 className="font-heading font-bold text-[17px] text-navy">Enquiry tracking</h3>
                </div>
                <TrackShipment compact />
                <p className="mt-4 text-[12.5px] text-muted-foreground">
                  Need full schedules? <a href="/track" className="text-teal font-semibold">Open the tracking &amp; schedules page</a>.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </ServicePageTemplate>
  );
}
